import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AppBar from '@material-ui/core/AppBar'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { ToolTipText } from 'components/ActionBar/ToolTipText'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { useWorkspace2Actions } from 'components/WorkspaceEditor/Workspace2Store'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { useFirmCreatedDate } from 'shared/hooks/useFirmCreatedDate'
import { useShouldHideWorkspaceToggle } from 'shared/hooks/useShouldHideWorkspaceToggle'
import { useWorkspaceToggleSync } from 'shared/hooks/useWorkspaceToggleSync'
import { getNestedObjectPropertyValue } from 'shared/utils/objectUtils'
import {
    getFromLocalStorage,
    updatePersistentConfigs,
} from 'shared/utils/workspaceUtils'
import ShowHideToggleButton from './ShowHideToggleButton'
import { WorkspaceActionBarContext } from './WorkspaceActionBarContext'

interface styleProps {
    isUserRedirectedFromStudio: boolean | undefined
}

const useShowHideBarStyles = makeStyles((theme: Theme) => ({
    showHideContainer: {
        background: (props: styleProps) =>
            props.isUserRedirectedFromStudio
                ? 'linear-gradient(89.95deg, #78869D 0.04%, #2F3843 99.96%)'
                : 'linear-gradient(89.95deg, #EB5757 0.04%, #C68226 99.96%);',
        height: 47,
        position: 'absolute',
        left: 0,
        top: 0,
        display: 'flex',
        justifyContent: 'center',
        fontSize: pxToRem(14),
    },
    barTitle: {
        textTransform: 'uppercase',
        marginRight: theme.spacing(2.5),
        fontSize: pxToRem(14),
    },
    contentContainer: {
        padding: theme.spacing(0, 1.5),
    },
    switchContainer: {
        marginRight: theme.spacing(2),
    },
    disable: {
        pointerEvents: 'none',
        opacity: 0.7,
    },
    shortcutKeyContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: pxToRem(5),
        gap: pxToRem(3),
        color: '#185E8E',
        fontSize: pxToRem(10),
    },
}))

export interface ShowHideBarItem {
    label?: string
    stateKey: string
}

export interface ShowHideBarProps {
    items?: ShowHideBarItem[]
    selectedProjectNode?: ProjectElementNode | null
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    userId?: string
    projectId?: string
}

interface LocationState {
    from: string
    fromStudio?: boolean
    selectedElementIdFromDesignProgressTable?: string
}

const ShowHideBar: React.FC<ShowHideBarProps> = ({
    items = [],
    selectedProjectNode,
    selectedTopic,
    userId,
    projectId,
}) => {
    const { pathname, state } = useLocation()

    const { currentState, setStateValue } = React.useContext(
        WorkspaceActionBarContext
    )

    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const elementItemValue = useElementItemStore((s) => s.elementItemValue)
    const setElementItemValue = useElementItemStore(
        (s) => s.setElementItemValue
    )
    const isUserRedirectedFromStudio = React.useMemo(
        () =>
            (state as LocationState)?.fromStudio ||
            pathname.includes('/DraftSpecTextProject/') ||
            pathname.includes('/DraftMasterSpecProject/') ||
            pathname.includes(
                `/${currentUserActiveFirm?.bestPracticeProjectId}/`
            ),
        [state, pathname]
    )

    const isTopicSelected = React.useMemo(() => {
        return !!selectedTopic
    }, [selectedTopic])

    const classes = useShowHideBarStyles({
        isUserRedirectedFromStudio,
    })

    // Fetch firm creation date
    const { firmCreatedDate } = useFirmCreatedDate()

    // Check if workspace toggle should be hidden based on firm creation date
    const shouldHideWorkspaceToggle = useShouldHideWorkspaceToggle({
        firmCreatedDate,
    })

    const getClass = (label: string | undefined): string => {
        if (
            (label !== 'Project Properties' && label !== 'Track Changes') ||
            isTopicSelected
        )
            return ''
        if (selectedProjectNode && !selectedProjectNode?.isInProject)
            return classes.disable
        return ''
    }

    const savedStateValues = React.useMemo(() => {
        if (userId && projectId) {
            return (
                getFromLocalStorage(userId, projectId) ?? {
                    showHideStates: {
                        categories: true,
                        elements: true,
                        actionBar: false,
                        properties: true,
                        changes: true,
                    },
                }
            )
        }
    }, [userId, projectId])

    const handleToggleSwitchValueChange = React.useCallback(
        (stateKey: string) => {
            if (stateKey.includes('betaWorkspace')) {
                const currentValue = getFromLocalStorage(
                    userId ?? '',
                    'Global',
                    'betaWorkspaceToggleChecked'
                )
                // Default to false (classic workspace) if no value is stored (null)
                // New firms (>= cutoff date) will have toggle hidden via useShouldHideWorkspaceToggle
                const toggledValue = !(currentValue === null ? false : currentValue)

                if (toggledValue) {
                    setElementItemValue({
                        ...elementItemValue,
                        elementId: '',
                        elementType: '',
                        baseElementId: '',
                    })
                }

                updatePersistentConfigs(
                    userId,
                    'Global',
                    'betaWorkspaceToggleChecked',
                    toggledValue
                )

                setStateValue('showHideStates.betaWorkspace', toggledValue)
                return
            }

            setStateValue(
                stateKey,
                !getNestedObjectPropertyValue(stateKey, currentState),
                true
            )
        },
        [currentState, setStateValue]
    )

    const { switchWorkspace } = useWorkspace2Actions()

    // Synchronize workspace toggle based on firm creation date and user preferences
    useWorkspaceToggleSync(shouldHideWorkspaceToggle, userId, setStateValue, switchWorkspace)

    React.useEffect(() => {
        if (selectedProjectNode?.isInProject) {
            const { properties, changes } = currentState.showHideStates || {}
            const propertiesToggleValue: boolean = selectedProjectNode?.isInProject
                ? properties
                : false
            const changesToggleValue: boolean =
                changes === undefined
                    ? true
                    : selectedProjectNode?.isInProject
                        ? changes
                        : false
            let updatedObj = {
                ...currentState,
                showHideStates: {
                    ...currentState.showHideStates,
                    properties: propertiesToggleValue,
                    changes: changesToggleValue,
                },
            }
            setStateValue(
                'showHideStates.properties',
                getNestedObjectPropertyValue(
                    'showHideStates.properties',
                    updatedObj
                ),
                true
            )

            setStateValue(
                'showHideStates.changes',
                getNestedObjectPropertyValue(
                    'showHideStates.changes',
                    updatedObj
                ),
                true
            )
        }
    }, [selectedProjectNode?.isInProject, setStateValue])

    const checkedState = React.useCallback(
        (
            barItem: ShowHideBarItem,
            currentState: Record<string, any>,
            isInProject: boolean | undefined
        ) => {
            const { stateKey, label } = barItem
            if (
                label &&
                ['Project Properties', 'Track Changes'].includes(label) &&
                !isInProject
            ) {
                return false
            }
            return getNestedObjectPropertyValue(stateKey, currentState)
        },
        []
    )

    return (
        <AppBar
            elevation={0}
            position="relative"
            className={classes.showHideContainer}
        >
            <ContainerMaxWidthXl data-testid="workspaceShowHideBar">
                <GridContainer
                    className={classes.contentContainer}
                    alignItems="center"
                >
                    <GridItem>
                        <Typography className={classes.barTitle}>
                            Show / Hide:
                        </Typography>
                    </GridItem>
                    {items
                        .filter((item) =>
                            !(shouldHideWorkspaceToggle && item.stateKey.includes('betaWorkspace'))
                        )
                        .map(
                            (showHideItem: ShowHideBarItem) =>
                                showHideItem?.label !== 'Action Bar' && (
                                    <GridItem
                                        key={showHideItem.stateKey}
                                        className={classes.switchContainer}
                                    >
                                        <div
                                            data-testid={
                                                getClass(showHideItem?.label) === ''
                                                    ? 'enableProjectProperty'
                                                    : 'disableProjectProperty'
                                            }
                                            className={getClass(
                                                showHideItem?.label
                                            )}
                                            style={{ display: 'flex' }}
                                        >
                                            <ShowHideToggleButton
                                                checked={checkedState(
                                                    showHideItem,
                                                    currentState,
                                                    selectedProjectNode?.isInProject
                                                )}
                                                label={showHideItem.label}
                                                onChange={() =>
                                                    handleToggleSwitchValueChange(
                                                        showHideItem.stateKey
                                                    )
                                                }
                                            />
                                            {showHideItem?.stateKey.includes(
                                                'betaWorkspace'
                                            ) && !isUserRedirectedFromStudio && (
                                                    <ToolTipText
                                                        label=""
                                                        smallLabel="BETA"
                                                        customStyles={{
                                                            shortcutKeyContainer:
                                                                classes.shortcutKeyContainer,
                                                        }}
                                                    />
                                                )}
                                        </div>
                                    </GridItem>
                                )
                        )}
                </GridContainer>
            </ContainerMaxWidthXl>
        </AppBar>
    )
}

export default ShowHideBar
